import React from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Box from "@material-ui/core/Box";
import CloseIcon from "@material-ui/icons/Close";
import { IconButton } from "@material-ui/core";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import { FormErrors } from "../../components/Login/FormErrors";
import { userService } from "_services/user.service";
import ConfirmModal from "components/Modal/modal";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import { EMAIL_REGEX, FACILITY_ADMIN, DP_USER } from "__helpers/constants";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Slide,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";

/**
 * Manager "Facility Users" screen: lists all discharge planners + facility admins across
 * every facility in the caller manager's subtree, and adds a facility user to a chosen
 * facility. Mirrors the facility-admin Manage Users dialog, plus a Facility selector.
 *   - list:   GET  /org-admin/facility-users            (subtree, server-resolved)
 *   - add:    POST /facility-admin/users?role=          (facility from the dialog dropdown)
 *   - actions PUT  /facility-admin/users/{id}/status|role, POST .../resend-invite
 * All mutation endpoints already authorize a manager over their subtree facilities.
 */

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="right" ref={ref} {...props} />;
});

const headCells = [
  { id: "name", label: "Name" },
  { id: "facility", label: "Facility" },
  { id: "email", label: "Email" },
  { id: "type", label: "Type" },
  { id: "status", label: "Status" },
  { id: "action", label: "Actions" },
];

function roleLabel(role) {
  return role === FACILITY_ADMIN ? "Facility Admin" : "Discharge Planner";
}

class OrgFacilityUsers extends React.Component {
  constructor(props) {
    super(props);
    let spinner = document.getElementById("loadingSpinner");
    this.state = {
      loading: false,
      spinner: spinner,
      users: [],
      facilities: [], // subtree facilities for the Add dialog selector
      search: "",
      // invite dialog
      modalStatus: false,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      role: DP_USER,
      facilityId: "", // chosen facility for the new user
      formErrors: { firstName: "", lastName: "", email: "", phoneNumber: "", facilityId: "" },
      firstNameValid: false,
      lastNameValid: false,
      emailValid: false,
      phoneNumberValid: false,
      facilityValid: false,
      // confirm (status toggle / role change / resend)
      confirmModalStatus: false,
      confirmModalMessage: "",
      pendingAction: null, // { type: 'status' | 'role' | 'resend', user, newRole }
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.confirmModalSubmit = this.confirmModalSubmit.bind(this);
  }

  componentDidMount() {
    this.loadUsers();
    this.loadFacilities();
  }

  loadUsers = () => {
    if (this.state.spinner) this.state.spinner.removeAttribute("hidden", "true");
    fetch(apiPath.orgAdminFacilityUsers, {
      method: "GET",
      headers: generalAuthTokenHeader(),
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => this.setState({ users: Array.isArray(data) ? data : [] }))
      .catch(() => this.setState({ users: [] }))
      .finally(() => {
        if (this.state.spinner) this.state.spinner.setAttribute("hidden", "true");
      });
  };

  loadFacilities = () => {
    fetch(apiPath.orgAdminFacilities, { method: "GET", headers: generalAuthTokenHeader() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => this.setState({ facilities: Array.isArray(data) ? data : [] }))
      .catch(() => this.setState({ facilities: [] }));
  };

  // ----- invite dialog -----
  modalOpen = () => {
    this.setState({
      modalStatus: true,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      role: DP_USER,
      facilityId: "",
      formErrors: { firstName: "", lastName: "", email: "", phoneNumber: "", facilityId: "" },
      firstNameValid: false,
      lastNameValid: false,
      emailValid: false,
      phoneNumberValid: false,
      facilityValid: false,
    });
  };

  modalClose = () => {
    this.setState({ modalStatus: false });
  };

  handleChangeInput = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    this.setState({ [name]: value }, () => this.validateField(name, value));
  };

  handleRoleChange = (event) => {
    this.setState({ role: event.target.value });
  };

  handleFacilityChange = (event) => {
    const value = event.target.value;
    this.setState((s) => ({
      facilityId: value,
      facilityValid: !!value,
      formErrors: { ...s.formErrors, facilityId: value ? "" : "Facility is required." },
    }));
  };

  validateField = (fieldName, fieldValue) => {
    let fieldValidationErrors = this.state.formErrors;
    let firstNameValid = this.state.firstNameValid;
    let lastNameValid = this.state.lastNameValid;
    let emailValid = this.state.emailValid;
    let phoneNumberValid = this.state.phoneNumberValid;
    switch (fieldName) {
      case "firstName":
        firstNameValid = fieldValue.trim().length > 0;
        fieldValidationErrors.firstName = firstNameValid ? "" : "First name is required.";
        break;
      case "lastName":
        lastNameValid = fieldValue.trim().length > 0;
        fieldValidationErrors.lastName = lastNameValid ? "" : "Last name is required.";
        break;
      case "email":
        if (fieldValue.trim().length === 0) {
          emailValid = false;
          fieldValidationErrors.email = "Email is required.";
        } else if (!EMAIL_REGEX.test(fieldValue.trim())) {
          emailValid = false;
          fieldValidationErrors.email = "Please enter a valid email address.";
        } else {
          emailValid = true;
          fieldValidationErrors.email = "";
        }
        break;
      case "phoneNumber":
        if (fieldValue.trim().length === 0) {
          phoneNumberValid = false;
          fieldValidationErrors.phoneNumber = "Phone number is required.";
        } else if (!/^\d{6,15}$/.test(fieldValue.trim())) {
          phoneNumberValid = false;
          fieldValidationErrors.phoneNumber = "Please enter a valid phone number.";
        } else {
          phoneNumberValid = true;
          fieldValidationErrors.phoneNumber = "";
        }
        break;
      default:
        break;
    }
    this.setState({
      formErrors: fieldValidationErrors,
      firstNameValid,
      lastNameValid,
      emailValid,
      phoneNumberValid,
    });
  };

  validateForm() {
    return (
      this.state.firstNameValid &&
      this.state.lastNameValid &&
      this.state.emailValid &&
      this.state.phoneNumberValid &&
      this.state.facilityValid
    );
  }

  handleSubmit(event) {
    event.preventDefault();
    this.invite();
  }

  async invite() {
    const { facilityId } = this.state;
    if (!facilityId) return;
    const payload = {
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      email: this.state.email,
      phoneCode: "+1",
      phoneNumber: this.state.phoneNumber,
      twoFaPhoneCode: "+1",
      twoFaPhoneNumber: "",
      activated: true,
      facility_id: String(facilityId),
      imageUrl: "string",
      langKey: "en",
      login: "1",
    };
    this.setState({ loading: true });
    await fetch(`${apiPath.facilityAdminUsers}?role=${encodeURIComponent(this.state.role)}`, {
      method: "POST",
      headers: generalAuthTokenHeader(),
      body: JSON.stringify(payload),
    })
      .then((response) => {
        this.setState({ loading: false });
        if (response.ok) {
          userService.showNotification({
            title: "Success",
            message: "User created and invited.",
            type: "success",
          });
          this.modalClose();
          this.loadUsers();
        } else {
          userService.showNotification({
            title: "Facility Users",
            message: "Failed to create user.",
            type: "danger",
          });
        }
      })
      .catch(() => {
        userService.showNotification({
          title: "Facility Users",
          message: "Failed to create user.",
          type: "danger",
        });
        this.setState({ loading: false });
      });
  }

  // ----- confirm (status toggle / role change / resend) -----
  confirmStatus = (user) => {
    this.setState({
      pendingAction: { type: "status", user },
      confirmModalMessage: user.activated
        ? "Do you want to deactivate this user?"
        : "Do you want to activate this user?",
      confirmModalStatus: true,
    });
  };

  confirmRole = (user) => {
    const newRole = user.role === FACILITY_ADMIN ? DP_USER : FACILITY_ADMIN;
    this.setState({
      pendingAction: { type: "role", user, newRole },
      confirmModalMessage:
        newRole === FACILITY_ADMIN
          ? "Promote this user to Facility Admin?"
          : "Demote this user to Discharge Planner?",
      confirmModalStatus: true,
    });
  };

  confirmResend = (user) => {
    this.setState({
      pendingAction: { type: "resend", user },
      confirmModalMessage: "Resend the invite (a new temporary password) to this user?",
      confirmModalStatus: true,
    });
  };

  confirmModalClose = () => {
    this.setState({ confirmModalStatus: false });
  };

  async confirmModalSubmit() {
    const { pendingAction } = this.state;
    if (!pendingAction) return;
    const { type, user, newRole } = pendingAction;
    const fid = user.facilityId;
    let url;
    let method;
    if (type === "status") {
      url = `${apiPath.facilityAdminUsers}/${user.userId}/status?facilityId=${fid}&activated=${!user.activated}`;
      method = "PUT";
    } else if (type === "role") {
      url = `${apiPath.facilityAdminUsers}/${user.userId}/role?facilityId=${fid}&role=${encodeURIComponent(newRole)}`;
      method = "PUT";
    } else {
      url = `${apiPath.facilityAdminUsers}/${user.userId}/resend-invite?facilityId=${fid}`;
      method = "POST";
    }
    const successMsg =
      type === "status"
        ? "Status updated successfully"
        : type === "role"
        ? "Role updated successfully"
        : "Invite re-sent successfully";
    this.setState({ loading: true });
    await fetch(url, { method, headers: generalAuthTokenHeader() })
      .then((response) => {
        this.setState({ loading: false });
        this.confirmModalClose();
        if (response.ok) {
          userService.showNotification({ title: "Success", message: successMsg, type: "success" });
          this.loadUsers();
        } else {
          userService.showNotification({
            title: "Facility Users",
            message: "Action failed.",
            type: "danger",
          });
        }
      })
      .catch(() => {
        userService.showNotification({
          title: "Facility Users",
          message: "Failed to update user.",
          type: "danger",
        });
        this.setState({ loading: false });
        this.confirmModalClose();
      });
  }

  render() {
    const { facilities } = this.state;
    const q = (this.state.search || "").trim().toLowerCase();
    const fullName = (u) => ((u.firstName || "") + " " + (u.lastName || "")).trim();
    const visibleUsers = this.state.users
      .filter((u) => {
        if (!q) return true;
        return fullName(u).toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
      })
      .sort((a, b) => fullName(a).toLowerCase().localeCompare(fullName(b).toLowerCase()));
    return (
      <div id="manage-facility-users-index">
        <ConfirmModal
          title="Alert"
          message={this.state.confirmModalMessage}
          confirmModalStatus={this.state.confirmModalStatus}
          confirmModalClose={this.confirmModalClose}
          submit={this.confirmModalSubmit}
          loading={this.state.loading}
        />

        {/* Add facility user */}
        <Dialog
          open={this.state.modalStatus}
          maxWidth={"sm"}
          onClose={this.modalClose}
          TransitionComponent={Transition}
          aria-labelledby="form-dialog-title"
          className="add-modal"
        >
          <DialogTitle className="add-modal-title" id="form-dialog-title">
            Add Facility User
            <IconButton className="closeButton" aria-label="close" onClick={this.modalClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <form onSubmit={this.handleSubmit} noValidate>
            <DialogContent>
              <Box display="flex" justifyContent="flex-start" mb={2} mt={2}>
                <Box width={1 / 2} pr={1}>
                  <TextField
                    InputLabelProps={{ className: "required-label" }}
                    name="firstName"
                    value={this.state.firstName}
                    onChange={this.handleChangeInput}
                    data-validators="isRequired"
                    label="First Name"
                  />
                  <FormErrors show={!this.state.firstNameValid} formErrors={this.state.formErrors} fieldName="firstName" />
                </Box>
                <Box width={1 / 2} pl={1}>
                  <TextField
                    InputLabelProps={{ className: "required-label" }}
                    name="lastName"
                    value={this.state.lastName}
                    onChange={this.handleChangeInput}
                    data-validators="isRequired"
                    label="Last Name"
                  />
                  <FormErrors show={!this.state.lastNameValid} formErrors={this.state.formErrors} fieldName="lastName" />
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-start" mb={2}>
                <Box width={1 / 2} pr={1}>
                  <TextField
                    InputLabelProps={{ className: "required-label" }}
                    name="email"
                    value={this.state.email}
                    onChange={this.handleChangeInput}
                    data-validators="isRequired"
                    label="Email"
                  />
                  <FormErrors show={!this.state.emailValid} formErrors={this.state.formErrors} fieldName="email" />
                </Box>
                <Box width={1 / 2} pl={1}>
                  <TextField
                    InputLabelProps={{ className: "required-label" }}
                    name="phoneNumber"
                    value={this.state.phoneNumber}
                    onChange={this.handleChangeInput}
                    data-validators="isRequired"
                    label="Phone Number"
                  />
                  <FormErrors show={!this.state.phoneNumberValid} formErrors={this.state.formErrors} fieldName="phoneNumber" />
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-start" mb={2}>
                <Box width={1 / 2} pr={1}>
                  <TextField
                    select
                    fullWidth
                    name="facilityId"
                    value={this.state.facilityId}
                    onChange={this.handleFacilityChange}
                    label="Facility"
                    InputLabelProps={{ className: "required-label" }}
                  >
                    <MenuItem value="">-- select facility --</MenuItem>
                    {facilities.map((f) => (
                      <MenuItem key={f.facilityId} value={f.facilityId}>
                        {f.facilityName}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormErrors show={!this.state.facilityValid} formErrors={this.state.formErrors} fieldName="facilityId" />
                </Box>
                <Box width={1 / 2} pl={1}>
                  <TextField
                    select
                    fullWidth
                    name="role"
                    value={this.state.role}
                    onChange={this.handleRoleChange}
                    label="Role"
                  >
                    <MenuItem value={DP_USER}>Discharge Planner</MenuItem>
                    <MenuItem value={FACILITY_ADMIN}>Facility Admin</MenuItem>
                  </TextField>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions className="modal-actions" justify="center">
              <Button type="submit" className="btn1" disabled={!this.validateForm() || this.state.loading}>
                {this.state.loading && <CircularProgress size={24} className="buttonProgress" />}
                Add User
              </Button>
              <Button onClick={this.modalClose} className="btn2" color="primary">
                Cancel
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <GridContainer>
          <Card className={"advert-cover"}>
            <GridItem xs={12} sm={12} md={12}>
              <CardHeader color="success" className={"cusCardheader cusCardTitle"}>
                <h4>Facility Users</h4>
              </CardHeader>
            </GridItem>
            <div className={"custom-container"}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                <div className="search-btn-cover" style={{ float: "none" }}>
                  <input
                    type="text"
                    placeholder="Search by name or email."
                    className="search-btn"
                    value={this.state.search}
                    onChange={(e) => this.setState({ search: e.target.value })}
                  />
                  <i className="material-icons">search</i>
                </div>
                <div className="button-cover" style={{ float: "none" }}>
                  <button className="pointer btn1" onClick={this.modalOpen}>
                    Add Facility User
                  </button>
                </div>
              </div>
              <div className="tableResponsive" style={{ clear: "both" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {headCells.map((headCell) => (
                        <TableCell key={headCell.id} align="left">
                          {headCell.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleUsers.length ? (
                      visibleUsers.map((u) => (
                        <TableRow key={`${u.facilityId}-${u.userId}`}>
                          <TableCell>{(u.firstName || "") + " " + (u.lastName || "")}</TableCell>
                          <TableCell>{u.facilityName || `#${u.facilityId}`}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>{roleLabel(u.role)}</TableCell>
                          <TableCell>{u.activated ? "Active" : "Inactive"}</TableCell>
                          <TableCell>
                            <div style={{ display: "flex", flexWrap: "nowrap", gap: 8, alignItems: "center" }}>
                              <button type="button" className="pointer btn1" onClick={() => this.confirmRole(u)}>
                                {u.role === FACILITY_ADMIN ? "Demote" : "Promote"}
                              </button>
                              <button type="button" className="pointer btn1" onClick={() => this.confirmStatus(u)}>
                                {u.activated ? "Deactivate" : "Activate"}
                              </button>
                              {/* Resend invite action hidden for now
                              <button type="button" className="pointer btn2" onClick={() => this.confirmResend(u)}>
                                Resend invite
                              </button>
                              */}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan="6">
                          <div className="not-found-text">No facility users found.</div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </Card>
        </GridContainer>
      </div>
    );
  }
}

export default OrgFacilityUsers;
